/**
 * LevelingSystem - Handles player XP, levels, and rewards
 */

import { InventoryComponent } from '../items/InventoryComponent';
import { ItemRegistry } from '../items/ItemRegistry';

export interface LevelReward {
  level: number;
  items?: { itemId: string; quantity: number }[];
  abilities?: string[];  // Ability keys like 'fly', 'double_jump', etc.
  description: string;
}

// XP required for each level (cumulative)
const LEVEL_XP_REQUIREMENTS: number[] = [
  0,      // Level 0 (start)
  50,     // Level 1
  150,    // Level 2
  300,    // Level 3
  500,    // Level 4
  750,    // Level 5
  1100,   // Level 6
  1500,   // Level 7
  2000,   // Level 8
  2600,   // Level 9
  3500,   // Level 10 - Primitive guns unlock
  4500,   // Level 11
  5700,   // Level 12
  7100,   // Level 13
  8700,   // Level 14
  10500,  // Level 15
  12500,  // Level 16
  14800,  // Level 17
  17400,  // Level 18
  20300,  // Level 19
  23500,  // Level 20 - Modern guns unlock
  27000,  // Level 21
  30800,  // Level 22
  35000,  // Level 23
  39500,  // Level 24
  44500,  // Level 25
  50000,  // Level 26
  56000,  // Level 27
  62500,  // Level 28
  69500,  // Level 29
  77000,  // Level 30 - Futuristic guns unlock
  85000,  // Level 31
  93500,  // Level 32
  102500, // Level 33
  112000, // Level 34
  122000, // Level 35
  133000, // Level 36
  145000, // Level 37
  158000, // Level 38
  172000, // Level 39
  187000, // Level 40 - Max level
];

// Rewards for each level
const LEVEL_REWARDS: LevelReward[] = [
  {
    level: 0,
    description: 'Welcome, survivor!'
  },
  {
    level: 1,
    items: [{ itemId: 'torch', quantity: 3 }],
    description: 'Received 3 torches!'
  },
  {
    level: 2,
    abilities: ['fly'],
    items: [{ itemId: 'cooked_meat', quantity: 5 }],
    description: 'Unlocked Flying! (Hold SPACE)'
  },
  {
    level: 3,
    items: [
      { itemId: 'wood', quantity: 50 },
      { itemId: 'stone', quantity: 50 }
    ],
    description: 'Received building materials!'
  },
  {
    level: 4,
    items: [{ itemId: 'iron_ore', quantity: 20 }],
    description: 'Received iron ore!'
  },
  {
    level: 5,
    items: [
      { itemId: 'axe_iron', quantity: 1 },
      { itemId: 'pickaxe_iron', quantity: 1 }
    ],
    description: 'Received iron tools!'
  },
  {
    level: 6,
    items: [{ itemId: 'sword_iron', quantity: 1 }],
    abilities: ['double_damage'],
    description: 'Received iron sword! Attack power doubled!'
  },
  {
    level: 7,
    items: [
      { itemId: 'cooked_meat', quantity: 20 },
      { itemId: 'apple', quantity: 20 }
    ],
    description: 'Received food supplies!'
  },
  {
    level: 8,
    items: [{ itemId: 'torch', quantity: 10 }],
    abilities: ['night_vision'],
    description: 'Improved night vision!'
  },
  {
    level: 9,
    items: [
      { itemId: 'iron_ore', quantity: 50 },
      { itemId: 'fiber', quantity: 100 }
    ],
    description: 'Received crafting materials!'
  },
  {
    level: 10,
    items: [
      { itemId: 'wood', quantity: 100 },
      { itemId: 'stone', quantity: 100 }
    ],
    abilities: ['fast_build', 'primitive_guns'],
    description: 'Fast building unlocked! PRIMITIVE GUNS now available!'
  },
  // ==========================================
  // LEVEL 11-20: Primitive Gun Tier
  // ==========================================
  {
    level: 11,
    items: [
      { itemId: 'flintlock_pistol', quantity: 1 },
      { itemId: 'musket_ball', quantity: 30 }
    ],
    description: 'Received Flintlock Pistol and ammo!'
  },
  {
    level: 12,
    items: [
      { itemId: 'musket_ball', quantity: 50 },
      { itemId: 'iron_ore', quantity: 30 }
    ],
    description: 'Received ammunition and iron!'
  },
  {
    level: 13,
    items: [
      { itemId: 'musket', quantity: 1 },
      { itemId: 'musket_ball', quantity: 40 }
    ],
    description: 'Received Musket!'
  },
  {
    level: 14,
    items: [
      { itemId: 'buckshot', quantity: 40 },
      { itemId: 'iron_ore', quantity: 40 }
    ],
    description: 'Received buckshot ammo!'
  },
  {
    level: 15,
    items: [
      { itemId: 'blunderbuss', quantity: 1 },
      { itemId: 'buckshot', quantity: 50 }
    ],
    abilities: ['steady_aim'],
    description: 'Received Blunderbuss! Steady Aim unlocked!'
  },
  {
    level: 16,
    items: [
      { itemId: 'musket_ball', quantity: 100 },
      { itemId: 'buckshot', quantity: 50 }
    ],
    description: 'Received ammunition supplies!'
  },
  {
    level: 17,
    items: [
      { itemId: 'iron_ore', quantity: 60 },
      { itemId: 'cooked_meat', quantity: 30 }
    ],
    description: 'Received crafting materials!'
  },
  {
    level: 18,
    items: [
      { itemId: 'flintlock_pistol', quantity: 1 },
      { itemId: 'musket_ball', quantity: 80 }
    ],
    description: 'Received backup pistol!'
  },
  {
    level: 19,
    items: [
      { itemId: 'musket_ball', quantity: 150 },
      { itemId: 'buckshot', quantity: 80 }
    ],
    description: 'Received large ammo cache!'
  },
  {
    level: 20,
    items: [
      { itemId: 'pistol_9mm', quantity: 1 },
      { itemId: 'pistol_ammo', quantity: 60 }
    ],
    abilities: ['modern_guns'],
    description: 'MODERN GUNS unlocked! Received 9mm Pistol!'
  },
  // ==========================================
  // LEVEL 21-30: Modern Gun Tier
  // ==========================================
  {
    level: 21,
    items: [
      { itemId: 'pistol_ammo', quantity: 100 },
      { itemId: 'rifle_ammo', quantity: 50 }
    ],
    description: 'Received modern ammunition!'
  },
  {
    level: 22,
    items: [
      { itemId: 'smg', quantity: 1 },
      { itemId: 'pistol_ammo', quantity: 120 }
    ],
    description: 'Received SMG!'
  },
  {
    level: 23,
    items: [
      { itemId: 'rifle_ammo', quantity: 100 },
      { itemId: 'shotgun_shells', quantity: 50 }
    ],
    description: 'Received rifle and shotgun ammo!'
  },
  {
    level: 24,
    items: [
      { itemId: 'assault_rifle', quantity: 1 },
      { itemId: 'rifle_ammo', quantity: 90 }
    ],
    description: 'Received Assault Rifle!'
  },
  {
    level: 25,
    items: [
      { itemId: 'shotgun_pump', quantity: 1 },
      { itemId: 'shotgun_shells', quantity: 40 }
    ],
    abilities: ['quick_reload'],
    description: 'Received Pump Shotgun! Quick Reload unlocked!'
  },
  {
    level: 26,
    items: [
      { itemId: 'pistol_ammo', quantity: 200 },
      { itemId: 'rifle_ammo', quantity: 150 }
    ],
    description: 'Received massive ammo supply!'
  },
  {
    level: 27,
    items: [
      { itemId: 'sniper_rifle', quantity: 1 },
      { itemId: 'rifle_ammo', quantity: 50 }
    ],
    description: 'Received Sniper Rifle!'
  },
  {
    level: 28,
    items: [
      { itemId: 'shotgun_shells', quantity: 100 },
      { itemId: 'rifle_ammo', quantity: 100 }
    ],
    description: 'Received tactical ammunition!'
  },
  {
    level: 29,
    items: [
      { itemId: 'energy_cell', quantity: 50 },
      { itemId: 'plasma_cell', quantity: 30 }
    ],
    description: 'Received energy cells! Prepare for the future...'
  },
  {
    level: 30,
    items: [
      { itemId: 'laser_pistol', quantity: 1 },
      { itemId: 'energy_cell', quantity: 100 }
    ],
    abilities: ['futuristic_guns'],
    description: 'FUTURISTIC GUNS unlocked! Received Laser Pistol!'
  },
  // ==========================================
  // LEVEL 31-40: Futuristic Gun Tier
  // ==========================================
  {
    level: 31,
    items: [
      { itemId: 'energy_cell', quantity: 150 },
      { itemId: 'plasma_cell', quantity: 80 }
    ],
    description: 'Received energy supplies!'
  },
  {
    level: 32,
    items: [
      { itemId: 'laser_rifle', quantity: 1 },
      { itemId: 'energy_cell', quantity: 120 }
    ],
    description: 'Received Laser Rifle!'
  },
  {
    level: 33,
    items: [
      { itemId: 'plasma_cell', quantity: 150 },
      { itemId: 'rail_charge', quantity: 30 }
    ],
    description: 'Received plasma and rail charges!'
  },
  {
    level: 34,
    items: [
      { itemId: 'plasma_rifle', quantity: 1 },
      { itemId: 'plasma_cell', quantity: 100 }
    ],
    description: 'Received Plasma Rifle!'
  },
  {
    level: 35,
    items: [
      { itemId: 'pulse_smg', quantity: 1 },
      { itemId: 'energy_cell', quantity: 200 }
    ],
    abilities: ['energy_efficiency'],
    description: 'Received Pulse SMG! Energy Efficiency unlocked!'
  },
  {
    level: 36,
    items: [
      { itemId: 'plasma_cannon', quantity: 1 },
      { itemId: 'plasma_cell', quantity: 150 }
    ],
    description: 'Received Plasma Cannon!'
  },
  {
    level: 37,
    items: [
      { itemId: 'energy_cell', quantity: 300 },
      { itemId: 'plasma_cell', quantity: 200 },
      { itemId: 'rail_charge', quantity: 50 }
    ],
    description: 'Received massive energy cache!'
  },
  {
    level: 38,
    items: [
      { itemId: 'rail_charge', quantity: 100 }
    ],
    description: 'Received rail charges for the ultimate weapon...'
  },
  {
    level: 39,
    items: [
      { itemId: 'railgun', quantity: 1 },
      { itemId: 'rail_charge', quantity: 50 }
    ],
    description: 'Received RAILGUN! The pinnacle of destruction!'
  },
  {
    level: 40,
    items: [
      { itemId: 'railgun', quantity: 1 },
      { itemId: 'plasma_cannon', quantity: 1 },
      { itemId: 'rail_charge', quantity: 200 },
      { itemId: 'plasma_cell', quantity: 300 }
    ],
    abilities: ['master_gunner'],
    description: 'MAX LEVEL! Master Gunner unlocked! Ultimate arsenal received!'
  },
];

export class LevelingSystem {
  private currentXP: number = 0;
  private currentLevel: number = 0;
  private unlockedAbilities: Set<string> = new Set();

  // Callbacks
  public onLevelUp: ((level: number, reward: LevelReward) => void) | null = null;
  public onXPGained: ((amount: number, total: number) => void) | null = null;

  constructor() {
    // Start at level 0
    this.currentLevel = 0;
    this.currentXP = 0;
  }

  /** Add XP and check for level ups */
  addXP(amount: number, inventory?: InventoryComponent): void {
    this.currentXP += amount;
    this.onXPGained?.(amount, this.currentXP);

    // Check for level ups
    while (this.canLevelUp()) {
      this.levelUp(inventory);
    }
  }

  /** Check if player has enough XP to level up */
  private canLevelUp(): boolean {
    const nextLevel = this.currentLevel + 1;
    if (nextLevel >= LEVEL_XP_REQUIREMENTS.length) return false;

    const requiredXP = LEVEL_XP_REQUIREMENTS[nextLevel];
    return requiredXP !== undefined && this.currentXP >= requiredXP;
  }

  /** Level up and grant rewards */
  private levelUp(inventory?: InventoryComponent): void {
    this.currentLevel++;

    const reward = LEVEL_REWARDS.find(r => r.level === this.currentLevel);
    if (reward) {
      // Grant items
      if (reward.items && inventory) {
        for (const item of reward.items) {
          const itemDef = ItemRegistry.get(item.itemId);
          if (itemDef) {
            inventory.addItem(itemDef, item.quantity);
          }
        }
      }

      // Unlock abilities
      if (reward.abilities) {
        for (const ability of reward.abilities) {
          this.unlockedAbilities.add(ability);
        }
      }

      this.onLevelUp?.(this.currentLevel, reward);
    }
  }

  /** Check if an ability is unlocked */
  hasAbility(ability: string): boolean {
    return this.unlockedAbilities.has(ability);
  }

  /** Get current level */
  getLevel(): number {
    return this.currentLevel;
  }

  /** Get current XP */
  getXP(): number {
    return this.currentXP;
  }

  /** Get XP required for next level */
  getXPForNextLevel(): number {
    const nextLevel = this.currentLevel + 1;
    if (nextLevel >= LEVEL_XP_REQUIREMENTS.length) {
      return LEVEL_XP_REQUIREMENTS[LEVEL_XP_REQUIREMENTS.length - 1] ?? 0;
    }
    return LEVEL_XP_REQUIREMENTS[nextLevel] ?? 0;
  }

  /** Get XP required for current level */
  getXPForCurrentLevel(): number {
    return LEVEL_XP_REQUIREMENTS[this.currentLevel] ?? 0;
  }

  /** Get progress to next level (0-1) */
  getLevelProgress(): number {
    const currentLevelXP = this.getXPForCurrentLevel();
    const nextLevelXP = this.getXPForNextLevel();
    const xpIntoLevel = this.currentXP - currentLevelXP;
    const xpNeeded = nextLevelXP - currentLevelXP;

    if (xpNeeded <= 0) return 1;
    return Math.min(1, xpIntoLevel / xpNeeded);
  }

  /** Check if at max level */
  isMaxLevel(): boolean {
    return this.currentLevel >= LEVEL_XP_REQUIREMENTS.length - 1;
  }

  /** Get save data */
  toSaveData(): { xp: number; level: number; abilities: string[] } {
    return {
      xp: this.currentXP,
      level: this.currentLevel,
      abilities: Array.from(this.unlockedAbilities)
    };
  }

  /** Load from save data */
  fromSaveData(data: { xp: number; level: number; abilities?: string[] }): void {
    this.currentXP = data.xp;
    this.currentLevel = data.level;
    this.unlockedAbilities.clear();

    if (data.abilities) {
      for (const ability of data.abilities) {
        this.unlockedAbilities.add(ability);
      }
    } else {
      // Rebuild abilities from level rewards if not saved
      for (const reward of LEVEL_REWARDS) {
        if (reward.level <= this.currentLevel && reward.abilities) {
          for (const ability of reward.abilities) {
            this.unlockedAbilities.add(ability);
          }
        }
      }
    }
  }

  /** Get max level */
  getMaxLevel(): number {
    return LEVEL_XP_REQUIREMENTS.length - 1;
  }
}
